import React, { useCallback, useState } from 'react'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useFocusEffect, useNavigation } from '@react-navigation/native'

// Components
import Typography from 'APP/Converse/Typography'
import Button from 'APP/Converse/Button'
import { ResponsiveView } from 'APP/Converse/Layout'
import ChallengedInvitationFooter from 'APP/Components/ChallengedInvitationFooter'
import TextInput from 'APP/Converse/Inputs/TextInput'
import Spinner from 'APP/Converse/Spinner'

// Services
import I18n from 'APP/I18n'
import Analytics from 'APP/Services/Analytics'

// State management
import { useSelector, useDispatch } from 'APP/Hooks/redux'
import PatientActions from 'APP/Redux/PatientRedux'
import { eligibilitySearchActions, eligibilitySearchSelectors } from 'APP/Store/EligibilitySearch'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const AccessCheckLoadingContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  flex: 1;
  justify-content: space-between;
`

const InnerContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const TrustBasedEligibilitySearchScreen = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const navigation = useNavigation()

  const analyticsName = 'trust_based_eligibility_search'

  const dispatch = useDispatch()

  const eligibilityAccessLoading = useSelector(
    eligibilitySearchSelectors.selectEligibilityAccessVerificationLoading
  )

  // We need to determine if the member is still onboarding or
  // in the invitation queue to decide whether to show the "skip" footer
  // This flow follows the same pattern as the EligibilityClaimScreen
  const { challengedInvitationQueue, isOnboarding } = useSelector((state) => state.patient)

  const isLoading = useSelector(eligibilitySearchSelectors.selectEligibilitySearchLoading)

  useFocusEffect(
    useCallback(() => {
      dispatch(eligibilitySearchActions.checkEligibilityAccess())
    }, [dispatch])
  )

  if (eligibilityAccessLoading) {
    return (
      <AccessCheckLoadingContainer testID="TRUST_BASED_ELIGIBILITY_ACCESS_CHECK_LOADING">
        <Spinner size="large" />
      </AccessCheckLoadingContainer>
    )
  }

  const handleSearch = () => {
    if (searchQuery.trim().length === 0) return

    Analytics.trackEvent(`${analyticsName}_query`, {
      search_term: searchQuery,
    })

    setSearchQuery('')
    dispatch(eligibilitySearchActions.searchOrganizations(searchQuery.trim()))
  }

  const isInChallengedQueue = (challengedInvitationQueue ?? []).length > 0

  const handleFocus = () => {
    Analytics.trackEvent('input_focus', {
      name: `${analyticsName}_search_bar`,
    })
  }

  const onboardingDeferredEnrolmentComplete = () => {
    dispatch(PatientActions.onboardingDeferredEnrolmentComplete())
  }

  const navigateToClaimAccessScreen = () => {
    navigation.navigate('claimAccessScreen')
  }

  return (
    <ScreenContainer testID="TRUST_BASED_ELIGIBILITY_SEARCH_SCREEN">
      <ContentContainer>
        <InnerContentContainer>
          <Typography variant="h1">{I18n.t('TrustBasedEligibilitySearch.title')}</Typography>
          <Typography variant="bodyNormal">
            {I18n.t('TrustBasedEligibilitySearch.description')}
          </Typography>
          <TextInput
            icon="search"
            iconVariant="materialIcons"
            label={I18n.t('TrustBasedEligibilitySearch.input.label')}
            value={searchQuery}
            onChangeText={setSearchQuery}
            maxLength={100}
            testID="TRUST_BASED_ELIGIBILITY_SEARCH"
            noBottomMargin
            onSubmitEditing={handleSearch}
            onFocus={handleFocus}
          />
          <Button
            title={I18n.t('TrustBasedEligibilitySearch.button')}
            widthVariant="full"
            testID="TRUST_BASED_ELIGIBILITY_SEARCH_BUTTON"
            disabled={searchQuery.trim().length === 0 || isLoading}
            isLoading={isLoading}
            onPress={handleSearch}
          />

          <Typography
            variant="link"
            testID="TRUST_BASED_ELIGIBILITY_ACCESS_CODE"
            onPress={navigateToClaimAccessScreen}
          >
            {I18n.t('TrustBasedEligibilitySearch.accessCode')}
          </Typography>
        </InnerContentContainer>

        {(isOnboarding || isInChallengedQueue) && (
          <ChallengedInvitationFooter
            analyticsName={analyticsName}
            onPress={() => isOnboarding && onboardingDeferredEnrolmentComplete()}
          />
        )}
      </ContentContainer>
    </ScreenContainer>
  )
}

export default TrustBasedEligibilitySearchScreen
